import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdatePublisherMutation } from "../../../api/publisherApiSlice";
import { useForm, Yup } from "../../forms/form";
import { useGetPublisherAdminQuery } from "../../../api/publisherAdminApiSlice";
import { useHistory, useParams } from "react-router-dom";
import Title from "../../Title";
import { Button, Col, Form, Row } from "react-bootstrap";
import Icon from "../../Icon";
import { BoolSelect, FormGroup, Select } from "../../components";
import { BillingInput } from "./billing_form/BillingInput";
import Block from "../../Block";
import { useGetPublisherUsersQuery } from "../../../api/publisherUserApiSlice";

export default function PublisherEdit(props) {
  const { t } = useTranslation();
  const publisherId = useParams()["publisherId"];
  const history = useHistory();
  const {
    data: publisher,
    isSuccess,
    isError,
    retry,
  } = useGetPublisherAdminQuery(publisherId);
  const {
    data: users = [],
    isSuccess: isSuccessUsers,
    isError: isErrorUsers,
  } = useGetPublisherUsersQuery(publisherId);
  const [update] = useUpdatePublisherMutation();

  const handleCancel = () =>
    history.push(`/publishers/${publisher.slug}/admin`);

  const initialValues = {
    name: "",
    enabled: "",
    default_beacon_mode: true,
    can_configure_cmp: false,
    has_devices_view: false,
    mrg_pricing: "",
    rev_share: "",
    production_threshold: "",
    is_terminated: false,
    terminated_at: "",
    billing_address: "",
    billing_entity: "",
    billing_manager_id: "",
  };
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    enabled: Yup.boolean(),
    default_beacon_mode: Yup.boolean(),
    has_devices_view: Yup.boolean(),
    can_configure_cmp: Yup.boolean(),
    mrg_pricing: Yup.object(),
    rev_share: Yup.number(),
    production_threshold: Yup.number().allowEmptyString(),
    billing_address: Yup.string(),
    billing_entity: Yup.string(),
    billing_manager_id: Yup.number(),
  });
  const { formData, setFormData, getInputProps, formProps, submitButtonProps } =
    useForm({
      defaultObject: publisher,
      initialValues: initialValues,
      validationSchema: validationSchema,
      onSubmit: (data) => {
        update({ publisherId, ...data })
          .unwrap()
          .then(handleCancel);
      },
    });

  useEffect(() => {
    setFormData(publisher);
  }, [publisher]);

  if (!isSuccess) {
    return <></>;
  }

  return (
    <>
      <Title
        main={t("menu.publishers")}
        sub={publisher.name}
        group={false}
        onBack={handleCancel}
      >
        <Button
          key="submit"
          type="submit"
          variant="primary"
          {...submitButtonProps}
        >
          <Icon icon="save" text={t("save")} />
        </Button>
        <Button variant="secondary" key="cancel" onClick={handleCancel}>
          <Icon icon="cancel" text={t("cancel")} />
        </Button>
      </Title>
      <Form {...formProps}>
        <Block title="General">
          <FormGroup
            xs={12}
            label={t("publisher.name")}
            input="text"
            {...getInputProps("name")}
          />
          <FormGroup
            input={BoolSelect}
            label={t("publisher.enabled")}
            {...getInputProps("enabled")}
          />
          <FormGroup
            input={BoolSelect}
            label={t("publisher.default_beacon_mode")}
            {...getInputProps("default_beacon_mode")}
          />
          <FormGroup
            input={BoolSelect}
            label={t("publisher.can_configure_cmp")}
            {...getInputProps("can_configure_cmp")}
          />
          <FormGroup
            input={BoolSelect}
            label={t("publisher.has_devices_view")}
            {...getInputProps("has_devices_view")}
          />
          <FormGroup
            label={t("publisher.production_threshold")}
            input="number"
            {...getInputProps("production_threshold")}
          />
          <Row>
            <FormGroup
              as={Col}
              label={t("publisher.is_terminated")}
              {...getInputProps("is_terminated")}
            >
              <BoolSelect />
            </FormGroup>
            <FormGroup
              as={Col}
              label={t("publisher.terminated_at")}
              {...getInputProps("terminated_at")}
            >
              <Form.Control type="datetime-local" />
            </FormGroup>
          </Row>
        </Block>
        <Block title="Billing">
          <BillingInput
            value={getInputProps("mrg_pricing").value}
            onChange={getInputProps("mrg_pricing").onChange}
          />
          <FormGroup
            label={t("publisher.rev_share")}
            input="number"
            {...getInputProps("rev_share")}
          />
          <FormGroup
            label={t("publisher.billing_entity")}
            input="text"
            {...getInputProps("billing_entity")}
          />
          <FormGroup
            label={t("publisher.billing_address")}
            input="textarea"
            {...getInputProps("billing_address")}
          />
          {isSuccessUsers && (
            <FormGroup
              label={t("publisher.billing_manager")}
              placeholder={t("pleaseSelect")}
              {...getInputProps("billing_manager_id")}
            >
              <Select>
                {users.map((u) => {
                  return (
                    <Option key={u.id} value={u.id}>
                      {u.fullName}
                    </Option>
                  );
                })}
              </Select>
            </FormGroup>
          )}
        </Block>
      </Form>
    </>
  );
}
